import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, Input, Steps, Typography } from "antd";
import {
  ArrowLeftOutlined,
  KeyOutlined,
  LockOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { useDispatch, useSelector } from "react-redux";
import { setAuth, setUser } from "../redux/commonSlice";
import AuthService from "../services/auth.services";
import "./login.css";

const { Title, Text } = Typography;

const isValidUsername = (value) =>
  value.length >= 3 && value.length <= 20 && /^[a-zA-Z0-9]+$/i.test(value);

const getLoginAlertType = (text) => {
  const normalized = String(text || "").toLowerCase();
  if (["created", "updated", "verified"].some((hint) => normalized.includes(hint))) {
    return "success";
  }
  return "error";
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [authMode, setAuthMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const [forgetStep, setForgetStep] = useState(1);
  const [fpEmail, setFpEmail] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpPassword, setFpPassword] = useState("");
  const [fpConfirmPassword, setFpConfirmPassword] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpMessage, setFpMessage] = useState("");

  const [signupStep, setSignupStep] = useState(1);
  const [suEmail, setSuEmail] = useState("");
  const [suOtp, setSuOtp] = useState("");
  const [suUsername, setSuUsername] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [suConfirmPassword, setSuConfirmPassword] = useState("");
  const [suLoading, setSuLoading] = useState(false);
  const [suMessage, setSuMessage] = useState("");

  const currentUser = useSelector((state) => state.common.user);

  const resetForgotFlow = () => {
    setForgetStep(1);
    setFpOtp("");
    setFpPassword("");
    setFpConfirmPassword("");
    setFpMessage("");
  };

  const resetSignupFlow = () => {
    setSignupStep(1);
    setSuOtp("");
    setSuUsername("");
    setSuPassword("");
    setSuConfirmPassword("");
    setSuMessage("");
  };

  const openForgotPassword = () => {
    setAuthMode("forgot");
    resetForgotFlow();
    setMessage("");
  };

  const openSignup = () => {
    setAuthMode("signup");
    resetSignupFlow();
    setMessage("");
  };

  const backToLoginMode = () => {
    setAuthMode("login");
    resetForgotFlow();
    resetSignupFlow();
    setMessage("");
  };

  const onChangeUsername = (e) => {
    setUsername(e.target.value);
  };

  const onChangePassword = (e) => {
    setPassword(e.target.value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setMessage("");
    setLoading(true);
    if (!username.trim() || !password.trim()) {
      setMessage("Username and password are required.");
      setLoading(false);
      return;
    }

    try {
      dispatch(setAuth({ isAuthenticating: true, isAuthenticated: false }));
      const authRes = await AuthService.login(username, password);

      const user = AuthService.getCurrentUser();
      dispatch(setUser(user));
      dispatch(setAuth({ isAuthenticating: false, isAuthenticated: !!user }));

      if (authRes?.accessToken) {
        navigate("/app");
      } else {
        const resMessage =
          (authRes.response && authRes.response.data && authRes.response.data.message) ||
          authRes.message ||
          authRes.toString();
        setMessage(resMessage);
      }
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setMessage(resMessage);
      dispatch(setAuth({ isAuthenticating: false, isAuthenticated: false }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      navigate("/app");
    }
  }, [currentUser, navigate]);

  const handleSendOtp = async () => {
    if (!fpEmail.trim()) {
      setFpMessage("Please enter your email.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.forgetPassword(fpEmail.trim());
      setForgetStep(2);
      setFpMessage("Verification code sent. Please check your inbox (and spam folder).");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!fpOtp.trim()) {
      setFpMessage("Please enter the verification code.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.verifyOTP(fpEmail.trim(), fpOtp.trim(), "password_reset");
      setForgetStep(3);
      setFpMessage("Code verified. You can now set a new password.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!fpPassword || !fpConfirmPassword) {
      setFpMessage("Please enter and confirm your new password.");
      return;
    }

    if (fpPassword !== fpConfirmPassword) {
      setFpMessage("Passwords do not match.");
      return;
    }

    if (fpPassword.length < 6) {
      setFpMessage("Password must be at least 6 characters.");
      return;
    }

    try {
      setFpLoading(true);
      setFpMessage("");
      await AuthService.updatePassword(fpEmail.trim(), fpPassword);
      setAuthMode("login");
      resetForgotFlow();
      setMessage("Password updated. Please sign in with your new password.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setFpMessage(resMessage);
    } finally {
      setFpLoading(false);
    }
  };

  const handleSendSignupOtp = async () => {
    if (!suEmail.trim()) {
      setSuMessage("Please enter your email.");
      return;
    }

    try {
      setSuLoading(true);
      setSuMessage("");
      await AuthService.sendSignupOtp(suEmail.trim());
      setSignupStep(2);
      setSuMessage("Verification code sent. Please check your inbox (and spam folder).");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setSuMessage(resMessage);
    } finally {
      setSuLoading(false);
    }
  };

  const handleVerifySignupOtp = async () => {
    if (!suOtp.trim()) {
      setSuMessage("Please enter the verification code.");
      return;
    }

    try {
      setSuLoading(true);
      setSuMessage("");
      await AuthService.verifyOTP(suEmail.trim(), suOtp.trim(), "signup");
      setSignupStep(3);
      setSuMessage("Email verified. Complete your free account details.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setSuMessage(resMessage);
    } finally {
      setSuLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!isValidUsername(suUsername.trim())) {
      setSuMessage("Username must be 3–20 characters and contain only letters and numbers.");
      return;
    }

    if (!suPassword || !suConfirmPassword) {
      setSuMessage("Please enter and confirm your password.");
      return;
    }

    if (suPassword !== suConfirmPassword) {
      setSuMessage("Passwords do not match.");
      return;
    }

    if (suPassword.length < 6) {
      setSuMessage("Password must be at least 6 characters.");
      return;
    }

    try {
      setSuLoading(true);
      setSuMessage("");
      await AuthService.register(suUsername.trim(), suEmail.trim(), suPassword);
      setAuthMode("login");
      resetSignupFlow();
      setUsername(suUsername.trim());
      setMessage("Free account created. Please sign in.");
    } catch (error) {
      const resMessage =
        (error.response && error.response.data && error.response.data.message) ||
        error.message ||
        error.toString();
      setSuMessage(resMessage);
    } finally {
      setSuLoading(false);
    }
  };

  const modeSubtitle =
    authMode === "signup"
      ? "Create a free account with email verification."
      : authMode === "forgot"
        ? "Reset your password with a verification code sent to your email."
        : "Sign in to manage endpoints, API keys, and traffic analytics.";

  return (
    <div className="auth-page">
      <div className="auth-bg-orb auth-bg-orb-one" />
      <div className="auth-bg-orb auth-bg-orb-two" />

      <Card className="auth-card" bordered={false}>
        <div className="auth-brand">
          <img src="/branding/onechain-logo.png" alt="OneChain" className="auth-logo" />
          <Title level={2} className="auth-title">OneChain API Platform</Title>
          <Text className="auth-subtitle">{modeSubtitle}</Text>
        </div>

        {authMode === "login" ? (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field">
              <label htmlFor="username">Username</label>
              <Input
                id="username"
                size="large"
                prefix={<UserOutlined />}
                placeholder="Enter your username"
                value={username}
                onChange={onChangeUsername}
                autoComplete="username"
              />
            </div>

            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <Input.Password
                id="password"
                size="large"
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                value={password}
                onChange={onChangePassword}
                autoComplete="current-password"
              />
            </div>

            <Button type="primary" htmlType="submit" size="large" loading={loading} className="auth-submit-btn" block>
              Login
            </Button>

            <div className="auth-links">
              <Button type="link" onClick={openForgotPassword} className="auth-link-btn">Forgot password?</Button>
              <Button type="link" onClick={openSignup} className="auth-link-btn">Create free account</Button>
            </div>

            {message && <Alert type={getLoginAlertType(message)} message={message} showIcon className="auth-alert" />}
          </form>
        ) : authMode === "forgot" ? (
          <div className="auth-forgot-wrap">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              className="auth-back-btn"
              onClick={backToLoginMode}
            >
              Back to login
            </Button>

            <Title level={4} className="auth-forgot-title">Reset Password</Title>
            <Steps
              size="small"
              current={forgetStep - 1}
              className="auth-step"
              items={[
                { title: "Email" },
                { title: "Code" },
                { title: "New Password" },
              ]}
            />

            <div className="auth-field">
              <label htmlFor="fpEmail">Email</label>
              <Input
                id="fpEmail"
                size="large"
                prefix={<MailOutlined />}
                placeholder="Enter your account email"
                value={fpEmail}
                onChange={(e) => setFpEmail(e.target.value)}
                disabled={forgetStep > 1 || fpLoading}
              />
            </div>

            {forgetStep >= 2 && (
              <div className="auth-field">
                <label htmlFor="fpOtp">Verification code</label>
                <Input
                  id="fpOtp"
                  size="large"
                  prefix={<KeyOutlined />}
                  placeholder="Enter code from email"
                  value={fpOtp}
                  onChange={(e) => setFpOtp(e.target.value)}
                  disabled={forgetStep > 2 || fpLoading}
                />
              </div>
            )}

            {forgetStep >= 3 && (
              <>
                <div className="auth-field">
                  <label htmlFor="fpPassword">New Password</label>
                  <Input.Password
                    id="fpPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="At least 6 characters"
                    value={fpPassword}
                    onChange={(e) => setFpPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="fpConfirmPassword">Confirm New Password</label>
                  <Input.Password
                    id="fpConfirmPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Re-enter new password"
                    value={fpConfirmPassword}
                    onChange={(e) => setFpConfirmPassword(e.target.value)}
                    disabled={fpLoading}
                  />
                </div>
              </>
            )}

            <div className="auth-forgot-actions">
              {forgetStep === 1 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleSendOtp}>
                  Send verification code
                </Button>
              )}

              {forgetStep === 2 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleVerifyOtp}>
                  Verify code
                </Button>
              )}

              {forgetStep === 3 && (
                <Button type="primary" size="large" block loading={fpLoading} onClick={handleResetPassword}>
                  Update Password
                </Button>
              )}
            </div>

            {fpMessage && (
              <Alert
                type={fpMessage.includes("sent") || fpMessage.includes("verified") ? "success" : "info"}
                message={fpMessage}
                showIcon
              />
            )}
          </div>
        ) : (
          <div className="auth-forgot-wrap">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              className="auth-back-btn"
              onClick={backToLoginMode}
            >
              Back to login
            </Button>

            <Title level={4} className="auth-forgot-title">Create Free Account</Title>
            <Text type="secondary" className="auth-signup-hint">
              Free accounts include monthly API quota. Contact OneChain to upgrade when you need more.
            </Text>
            <Steps
              size="small"
              current={signupStep - 1}
              className="auth-step"
              items={[
                { title: "Email" },
                { title: "Code" },
                { title: "Account" },
              ]}
            />

            <div className="auth-field">
              <label htmlFor="suEmail">Email</label>
              <Input
                id="suEmail"
                size="large"
                prefix={<MailOutlined />}
                placeholder="Enter your email"
                value={suEmail}
                onChange={(e) => setSuEmail(e.target.value)}
                disabled={signupStep > 1 || suLoading}
              />
            </div>

            {signupStep >= 2 && (
              <div className="auth-field">
                <label htmlFor="suOtp">Verification code</label>
                <Input
                  id="suOtp"
                  size="large"
                  prefix={<KeyOutlined />}
                  placeholder="Enter code from email"
                  value={suOtp}
                  onChange={(e) => setSuOtp(e.target.value)}
                  disabled={signupStep > 2 || suLoading}
                />
              </div>
            )}

            {signupStep >= 3 && (
              <>
                <div className="auth-field">
                  <label htmlFor="suUsername">Username</label>
                  <Input
                    id="suUsername"
                    size="large"
                    prefix={<UserOutlined />}
                    placeholder="3–20 letters or numbers"
                    value={suUsername}
                    onChange={(e) => setSuUsername(e.target.value)}
                    disabled={suLoading}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="suPassword">Password</label>
                  <Input.Password
                    id="suPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="At least 6 characters"
                    value={suPassword}
                    onChange={(e) => setSuPassword(e.target.value)}
                    disabled={suLoading}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="suConfirmPassword">Confirm Password</label>
                  <Input.Password
                    id="suConfirmPassword"
                    size="large"
                    prefix={<LockOutlined />}
                    placeholder="Re-enter password"
                    value={suConfirmPassword}
                    onChange={(e) => setSuConfirmPassword(e.target.value)}
                    disabled={suLoading}
                  />
                </div>
              </>
            )}

            <div className="auth-forgot-actions">
              {signupStep === 1 && (
                <Button type="primary" size="large" block loading={suLoading} onClick={handleSendSignupOtp}>
                  Send verification code
                </Button>
              )}

              {signupStep === 2 && (
                <Button type="primary" size="large" block loading={suLoading} onClick={handleVerifySignupOtp}>
                  Verify email
                </Button>
              )}

              {signupStep === 3 && (
                <Button type="primary" size="large" block loading={suLoading} onClick={handleSignup}>
                  Create account
                </Button>
              )}
            </div>

            {suMessage && (
              <Alert
                type={suMessage.includes("sent") || suMessage.includes("verified") ? "success" : "info"}
                message={suMessage}
                showIcon
              />
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Login;
